import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ExpandableFilterPanel, {
  ExpandableFilterPanelItem,
} from 'components/molecules/expandable-filter-panel';
import TimezoneSelectBox from 'components/molecules/timezone-select-box';
import { debounce } from 'lodash';
import BooleanSelectBox from 'components/molecules/boolean-select-box';
import CampaignStatusSelectBoxSelectBox from 'components/molecules/sms-campaign/campaign-status-select-box';

import s from './styles.module.scss';
import SmsCampaignsFieldSearchFilter from './filed-search';

import type { TResetFilterFn } from 'infrastructure/hooks/common/use-query-param-new';
import type { ISmsCampaignsProgressFilters } from 'infrastructure/interfaces';

interface IProps {
  data: ISmsCampaignsProgressFilters;
  updateFilter: (data: ISmsCampaignsProgressFilters) => void;
  resetFilter: TResetFilterFn<ISmsCampaignsProgressFilters>;
  count: number;
}

const SmsCampaignsTableFilters: React.FC<IProps> = (props) => {
  const { data, updateFilter, resetFilter, count } = props;

  const { t } = useTranslation();

  const debouncedUpdateFilter = useMemo(
    () =>
      debounce((filters: ISmsCampaignsProgressFilters) => {
        updateFilter(filters);
      }, 300),
    [updateFilter],
  );

  return (
    <ExpandableFilterPanel
      filtersCount={count}
      onReset={() => {
        resetFilter();
      }}
    >
      <div className={s.form}>
        <ExpandableFilterPanelItem label={t('labels.recipientFilter')}>
          <SmsCampaignsFieldSearchFilter
            data={data}
            updateFilter={updateFilter}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.campaignStatus')}>
          <CampaignStatusSelectBoxSelectBox
            value={data.status}
            placeholder={t('labels.all')}
            withAll
            allowClear
            onChange={(status) => {
              debouncedUpdateFilter({
                status,
                page: undefined,
              });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem
          label={t('labels.contactAvailabilityStatus')}
        >
          <BooleanSelectBox
            value={data.available}
            trueLabel={t('labels.available')}
            falseLabel={t('labels.notAvailable')}
            placeholder={t('labels.all')}
            withAll
            allowClear
            onChange={(available) => {
              debouncedUpdateFilter({
                available,
                page: undefined,
              });
            }}
          />
        </ExpandableFilterPanelItem>
        <ExpandableFilterPanelItem label={t('labels.timezones')}>
          <TimezoneSelectBox
            value={data.timezone}
            placeholder={t('labels.all')}
            mode="multiple"
            allowClear
            withAll
            onChange={(timezones) => {
              debouncedUpdateFilter({
                timezone: timezones.includes(null) ? undefined : timezones,
                page: undefined,
              });
            }}
          />
        </ExpandableFilterPanelItem>
      </div>
    </ExpandableFilterPanel>
  );
};

export default SmsCampaignsTableFilters;
